import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useScrollSnap } from '../../hooks/useScrollSnap';
import {
  IonButton,
  IonIcon,
  IonContent,
  IonGrid,
  IonList,
  IonItem,
  IonLabel,
  IonSpinner,
} from '@ionic/react';
import {
  closeOutline,
  addCircleOutline,
  chevronForwardOutline,
  chatbubblesOutline,
  checkmarkCircle,
  alertCircleOutline,
  cardOutline,
  trashOutline,
  searchOutline,
  chevronBack,
} from 'ionicons/icons';
import BasePageWithHeader, { TitleCard } from '../BasePageWithHeader';
import styles from './TemplatesModal.module.scss';
import { getSavedReplies, addSavedReply, deleteSavedReply, type SavedReply } from '../../services/savedRepliesService';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

interface TemplatesModalProps {
  onDismiss: () => void;
  onSelectTemplate: (template: string) => void;
}

// i18n-ignore: message bodies are sent to clients exactly as written
const BUILT_IN_TEMPLATES = [
  { id: 'welcome', get title() { return i18n.t('components.chatComponents.TemplatesModal.welcomeMessage'); }, text: "Thank you for contacting us! How can we help you today?", icon: chatbubblesOutline, color: 'primary' },
  { id: 'thankyou', get title() { return i18n.t('components.chatComponents.TemplatesModal.thankYou'); }, text: "We appreciate your business and look forward to serving you again!", icon: chatbubblesOutline, color: 'primary' },
  { id: 'confirmed', get title() { return i18n.t('components.chatComponents.TemplatesModal.appointmentConfirmed'); }, text: "Your appointment has been confirmed. We will see you soon!", icon: checkmarkCircle, color: 'success' },
  { id: 'reminder', get title() { return i18n.t('components.chatComponents.TemplatesModal.appointmentReminder'); }, text: "This is a reminder about your upcoming appointment. Please let us know if you need to reschedule.", icon: alertCircleOutline, color: 'warning' },
  { id: 'payment', get title() { return i18n.t('components.chatComponents.TemplatesModal.paymentReceived'); }, text: "We have received your payment. Thank you!", icon: cardOutline, color: 'primary' },
];

export const TemplatesModal: React.FC<TemplatesModalProps> = ({
  onDismiss,
  onSelectTemplate,
}) => {
  const { t: tr } = useTranslation();
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const gridRef = useRef<HTMLIonGridElement>(null);

  const [customTemplates, setCustomTemplates] = useState<SavedReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newText, setNewText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSavedReplies()
      .then(setCustomTemplates)
      .catch(err => console.error('[TemplatesModal] Failed to load saved replies:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!newTitle.trim() || !newText.trim()) return;
    setSaving(true);
    try {
      const saved = await addSavedReply(newTitle.trim(), newText.trim());
      setCustomTemplates(prev => [...prev, saved]);
      setNewTitle('');
      setNewText('');
      setShowAddForm(false);
    } catch (err) {
      console.error('[TemplatesModal] Failed to save template:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSavedReply(id);
      setCustomTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('[TemplatesModal] Failed to delete template:', err);
    }
  };

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(gridRef, 66, applyProgress);

  return (
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={tr('components.chatComponents.TemplatesModal.savedReplies')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        showAddForm ? (
          <IonButton shape="round" className={styles.backButton} onClick={() => { setShowAddForm(false); setNewTitle(''); setNewText(''); }}>
            <IonIcon icon={chevronBack} slot="icon-only" />
          </IonButton>
        ) : (
          <IonButton shape="round" className={styles.backButton} onClick={onDismiss}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        )
      }
      endButtons={
        <IonButton className={styles.hiddenButton} style={{ visibility: 'hidden' }}>
          <IonIcon icon={searchOutline} />
        </IonButton>
      }
    >
      <IonContent scrollY={false} className={styles.pageContent}>
        <IonGrid
          ref={gridRef}
          className={styles.templatesContainer}
          onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
        >
          <TitleCard
            ref={titleCardAnimationRef}
            title={showAddForm ? tr('components.chatComponents.TemplatesModal.newTemplate') : tr('components.chatComponents.TemplatesModal.savedReplies')}
            subtitle={showAddForm ? tr('components.chatComponents.TemplatesModal.createAReusableReplyForYour') : tr('components.chatComponents.TemplatesModal.pickCreateAndManageSavedReplies')}
            subtitleClassName={styles.heroSubtitle}
            cardClassName={styles.templatesTitleCard}
            titleContainerClassName={styles.titleContainer}
            titleClassName={styles.templatesTitle}
          />

          {showAddForm ? (
            <div className={styles.templatesPanel}>
              <p className={styles.sectionEyebrow}>{tr('components.chatComponents.TemplatesModal.newSavedReply')}</p>
              <div className={styles.addFormContainer}>
                <input
                  className={styles.addFormInput}
                  placeholder={tr('components.chatComponents.TemplatesModal.title')}
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                />
                <textarea
                  className={styles.addFormTextarea}
                  placeholder={tr('components.chatComponents.TemplatesModal.messageText')}
                  rows={5}
                  value={newText}
                  onChange={e => setNewText(e.target.value)}
                />
                <IonButton
                  fill="solid"
                  disabled={!newTitle.trim() || !newText.trim() || saving}
                  onClick={handleSave}
                  className={styles.addFormSaveBtn}
                >
                  {saving ? <IonSpinner name="dots" /> : tr('components.chatComponents.TemplatesModal.saveTemplate')}
                </IonButton>
              </div>
            </div>
          ) : (
            <div className={styles.templatesPanel}>
              <p className={styles.sectionEyebrow}>{tr('components.chatComponents.TemplatesModal.savedRepliesLibrary')}</p>
              <IonList lines="none" className={styles.templatesList}>
                <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.templateItem} onClick={() => setShowAddForm(true)}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={addCircleOutline} />
                  </div>
                  <IonLabel className={styles.templateLabel}>
                    <h3>{tr('components.chatComponents.TemplatesModal.addNewTemplate')}</h3>
                    <p>{tr('components.chatComponents.TemplatesModal.createANewSavedReply')}</p>
                  </IonLabel>
                </IonItem>

                {loading && (
                  <IonItem lines="none" className={styles.templateItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeNeutral}`}>
                      <IonSpinner name="dots" />
                    </div>
                    <IonLabel className={styles.templateLabel}><p>{tr('components.chatComponents.TemplatesModal.loadingTemplates')}</p></IonLabel>
                  </IonItem>
                )}

                {BUILT_IN_TEMPLATES.map((template) => (
                  <IonItem
                    key={template.id}
                    button
                    detail
                    detailIcon={chevronForwardOutline}
                    lines="none"
                    className={styles.templateItem}
                    onClick={() => {
                      onSelectTemplate(template.text);
                      onDismiss();
                    }}
                  >
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={template.icon} />
                    </div>
                    <IonLabel className={styles.templateLabel}>
                      <h3>{template.title}</h3>
                      <p>{template.text}</p>
                    </IonLabel>
                  </IonItem>
                ))}

                {customTemplates.map((template) => (
                  <IonItem
                    key={template.id}
                    button
                    detail={false}
                    lines="none"
                    className={styles.templateItem}
                    onClick={() => {
                      onSelectTemplate(template.text);
                      onDismiss();
                    }}
                  >
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                      <IonIcon icon={chatbubblesOutline} />
                    </div>
                    <IonLabel className={styles.templateLabel}>
                      <h3>{template.title}</h3>
                      <p>{template.text}</p>
                    </IonLabel>
                    <IonButton
                      fill="clear"
                      slot="end"
                      className={styles.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(template.id);
                      }}
                    >
                      <IonIcon icon={trashOutline} slot="icon-only" />
                    </IonButton>
                  </IonItem>
                ))}
              </IonList>
            </div>
          )}

          <div className={styles.scrollSpacer} />
        </IonGrid>
      </IonContent>
    </BasePageWithHeader>
  );
};
